import { h, toast } from '../components/dom.js';
import { renderShell, pageHead, portalFooter } from '../components/shell.js';
import { api } from '../api/client.js';
import { formatCode, countdownFraction } from './code_helpers.js';

// renderCode shows the main-menu "Code" screen: a rolling 6-digit device-join
// code (TOTP-style, 30s) with a live countdown that auto-refreshes when the
// window rolls over. The raw device-join secret NEVER reaches the client — we
// only ever fetch the derived code + seconds_remaining from the server and
// re-fetch on rollover.
export async function renderCode(): Promise<void> {
  const main = renderShell('/code');
  main.append(pageHead('Add a device', 'Enter this code on your new device to add it to your account.'));

  const codeEl = h('div', { class: 'join-code-display', style: 'font-size:2.6rem;letter-spacing:.35rem;font-weight:700;text-align:center;font-variant-numeric:tabular-nums' }, '••• •••');
  const ring = h('div', { class: 'join-code-ring', style: 'text-align:center;color:var(--muted);margin-top:6px' }, '');
  const copyBtn = h('button', { class: 'btn btn-sm', type: 'button' }, 'Copy code');
  let currentCode = '';

  copyBtn.addEventListener('click', async () => {
    if (!currentCode) return;
    try { await navigator.clipboard.writeText(currentCode); toast('Code copied'); }
    catch { toast('Copy failed'); }
  });

  const card = h('div', { class: 'card', style: 'max-width:420px;margin:0 auto 22px;text-align:center' },
    codeEl, ring,
    h('div', { style: 'margin-top:16px' }, copyBtn),
    h('p', { class: 'muted', style: 'margin-top:18px' },
      'Enter this code on your new device to add it to your account. ',
      "You'll approve the request here on this trusted device."),
  );
  main.append(card);
  main.append(portalFooter());

  let timer: number | undefined;
  let disposed = false;

  const tickRing = (secondsRemaining: number, period: number) => {
    const frac = countdownFraction(secondsRemaining, period);
    const pct = Math.round(frac * 100);
    ring.textContent = `Refreshes in ${secondsRemaining}s`;
    ring.setAttribute('data-remaining', String(pct));
  };

  const fetchAndRun = async () => {
    if (disposed) return;
    try {
      const res = await api.auth.joinCode();
      if (disposed) return;
      currentCode = res.code;
      codeEl.textContent = formatCode(res.code);
      let remaining = res.seconds_remaining;
      const period = res.period || 30;
      tickRing(remaining, period);
      // local countdown; re-fetch when the window rolls over (secret stays server-side)
      timer = window.setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          window.clearInterval(timer);
          timer = undefined;
          void fetchAndRun();
          return;
        }
        tickRing(remaining, period);
      }, 1000);
    } catch {
      if (!disposed) { codeEl.textContent = 'error'; ring.textContent = 'Could not load code'; }
    }
  };

  await fetchAndRun();

  // Clean up the interval when navigating away (no leaked timers).
  const cleanup = () => {
    disposed = true;
    if (timer) { window.clearInterval(timer); timer = undefined; }
    window.removeEventListener('hashchange', cleanup);
  };
  window.addEventListener('hashchange', cleanup);
}
